import { fromPointArray } from "@virdx/wsi-viewer"
import { z } from "zod"

const scalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])

/**
 * Isolate a Uint8Array from a shared CBOR decode buffer.
 *
 * cbor-x returns typed-array views whose `.buffer` is the entire decode
 * arena (often 8 KB+). NiiVue's decodeRLE (and other callers) create
 * `new Uint8Array(rle.buffer)` which silently reads the whole arena
 * instead of the intended slice. Copying into an owned buffer
 * (byteOffset 0, byteLength === buffer.byteLength) prevents this.
 */
const isolatedBytes = z.instanceof(Uint8Array).transform((buf) => new Uint8Array(buf))

const maskSchema = z.object({
  id: z.string().min(1),
  seriesId: z.string().nullable(),
  projectModuleId: z.string().min(1),
  labelName: z.string().nullable(),
  labelValue: z.number().int().nullable(),
  instanceOrdinal: z.number().int().min(1),
  rle: isolatedBytes
})

const pointSchema = z.object({ x: z.number(), y: z.number() })

// Accept Int32Array (new format) or {x,y}[] (old CBOR sessions), normalize to Int32Array
function coordBufZod(minPoints?: number) {
  const arrSchema = minPoints ? z.array(pointSchema).min(minPoints) : z.array(pointSchema)
  return z
    .union([z.instanceof(Int32Array), arrSchema])
    .transform((val): Int32Array => (val instanceof Int32Array ? val : fromPointArray(val)))
}

const vectorSchema = z.object({
  id: z.string().min(1),
  seriesId: z.string().nullable(),
  projectModuleId: z.string().min(1),
  labelName: z.string().nullable(),
  labelValue: z.number().int().nullable(),
  annotation: z.object({
    id: z.string().min(1),
    imageName: z.string().min(1),
    points: coordBufZod(3),
    holes: z.array(coordBufZod()).optional(),
    style: z.object({
      strokeColor: z.string(),
      strokeOpacity: z.number(),
      fillColor: z.string(),
      fillOpacity: z.number(),
      strokeWidth: z.number().optional()
    }),
    label: z.string().optional(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
    bbox: z.object({
      left: z.number(),
      top: z.number(),
      right: z.number(),
      bottom: z.number()
    })
  })
})

const scoreSchema = z.object({
  id: z.string().min(1),
  seriesId: z.string().nullable(),
  projectModuleId: z.string().min(1),
  labelName: z.string().nullable(),
  labelValue: z.number().int().nullable(),
  values: z.record(z.string(), scalarSchema)
})

const imageLabelSchema = z.object({
  id: z.string().min(1),
  seriesId: z.string().nullable(),
  projectModuleId: z.string().min(1),
  labels: z.record(z.string(), z.boolean()),
  seriesMapping: z.record(z.string(), z.array(z.string())).optional()
})

const textSchema = z.object({
  id: z.string().min(1),
  seriesId: z.string().nullable(),
  projectModuleId: z.string().min(1),
  valueText: z.string()
})

// Runs are either inline (legacy / standalone) or a `runsRef` key into the
// session-level `labelRasterRunStore` (deduplicated across checkpoints).
const labelRasterChunkSchema = z
  .object({
    index: z.tuple([z.number().int().min(0), z.number().int().min(0)]),
    originalCrc32: z.number().int().min(0),
    runs: isolatedBytes.optional(),
    runsRef: z.string().min(1).optional(),
    changedPixels: z.number().int().min(0),
    bbox: z.tuple([z.number().int().min(0), z.number().int().min(0), z.number().int().min(0), z.number().int().min(0)])
  })
  .refine((c) => c.runs !== undefined || c.runsRef !== undefined, {
    message: "label raster chunk needs runs or runsRef"
  })

const labelRasterRunBlobSchema = z.object({
  key: z.string().min(1),
  runs: isolatedBytes
})

const labelRasterSchema = z.object({
  id: z.string().min(1),
  seriesId: z.string().min(1),
  baseSeriesId: z.string().nullable(),
  projectModuleId: z.string().min(1),
  labelName: z.string().nullable(),
  labelValue: z.null(),
  source: z.object({
    arrayPath: z.string().min(1),
    shape: z.tuple([z.number().int().min(0), z.number().int().min(0)]),
    chunkShape: z.tuple([z.number().int().min(1), z.number().int().min(1)]),
    dtype: z.enum(["uint8", "uint16"]),
    locator: z.string().optional(),
    taxonomy: z.string().optional(),
    pipelineVersion: z.string().optional()
  }),
  chunks: z.array(labelRasterChunkSchema),
  classesUsed: z.array(z.number().int().min(0)),
  editedAt: z.string().min(1)
})

const moduleCompletionSchema = z.object({
  projectModuleId: z.string().min(1),
  completed: z.boolean(),
  completedAt: z.string().nullable()
})

const snapshotSchema = z.object({
  masks: z.array(maskSchema),
  vectors: z.array(vectorSchema),
  scores: z.array(scoreSchema),
  imageLabels: z.array(imageLabelSchema),
  texts: z.array(textSchema),
  moduleCompletions: z.array(moduleCompletionSchema),
  // Added after v3 sessions shipped; older CBOR files omit it.
  labelRasters: z.array(labelRasterSchema).default([])
})

const checkpointSchema = z.object({
  timestamp: z.number().int(),
  reason: z.enum(["mutation", "save", "submit"]),
  snapshot: snapshotSchema
})

const maskNiftiSchema = z.object({
  annotationId: z.string().min(1),
  nifti: isolatedBytes
})

const labelRasterManifestEntrySchema = z.object({
  annotationId: z.string().min(1),
  manifest: isolatedBytes
})

export const sessionCborSchemaV1 = z.object({
  version: z.literal(3),
  submissionId: z.string().min(1),
  caseId: z.string().min(1),
  updatedAt: z.string().min(1),
  cursor: z.number().int().min(0),
  checkpoints: z.array(checkpointSchema).min(1),
  maskNiftis: z.array(maskNiftiSchema).optional(),
  labelRasterManifests: z.array(labelRasterManifestEntrySchema).optional(),
  labelRasterRunStore: z.array(labelRasterRunBlobSchema).optional()
})
